import { useState } from "react";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, fmtEuro, CLAIM_STATUS, CLAIM_REASONS } from "@/lib/api";
import { PublicShell } from "@/components/PublicShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ClaimStatusBadge } from "@/components/StatusBadge";

const Timeline = ({ items }) => (
  <ol className="relative border-l border-gray-200 ml-2 space-y-6 mt-6">
    {items.map((t, i) => (
      <li key={i} className="ml-6">
        <span className="absolute -left-[7px] mt-1 h-3.5 w-3.5 rounded-full bg-emerald-900 ring-4 ring-emerald-50" />
        <div className="flex items-center gap-3 flex-wrap"><ClaimStatusBadge status={t.status} /><time className="text-xs text-gray-500">{fmtDateTime(t.at)}</time></div>
        {t.message && <p className="mt-2 text-sm text-gray-700 whitespace-pre-line">{t.message}</p>}
      </li>
    ))}
  </ol>
);

export default function ClaimStatus() {
  const [f, setF] = useState({ number: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [claim, setClaim] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.get("/public/claims/status", { params: f });
      setClaim(data);
    } catch (err) {
      setClaim(null);
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicShell>
      <section className="max-w-2xl mx-auto px-5 sm:px-8 py-14">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight fade-up">Stato della pratica</h1>
        <p className="mt-3 text-gray-600">Inserisci il numero pratica (es. GR-1A2B3C) e l'email dell'ordine.</p>
        <form onSubmit={submit} className="mt-8 bg-white border border-gray-200 rounded-lg p-6 grid sm:grid-cols-[1fr_1fr_auto] gap-4 items-end" data-testid="claim-status-form">
          <div className="space-y-2"><Label>Numero pratica</Label><Input required value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} data-testid="claim-status-number-input" /></div>
          <div className="space-y-2"><Label>Email</Label><Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="claim-status-email-input" /></div>
          <Button type="submit" disabled={loading} className="bg-emerald-900 hover:bg-emerald-800 rounded-full" data-testid="claim-status-submit">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />} Cerca
          </Button>
        </form>
        {claim && (
          <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6 sm:p-8 fade-up" data-testid="claim-status-result">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="font-mono text-xl font-bold text-emerald-900">{claim.claim_number}</p>
                <p className="text-sm text-gray-600 mt-1">Ordine #{claim.order_number} · {CLAIM_REASONS[claim.reason]}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Stato attuale</p>
                <p className="font-display text-xl font-bold" data-testid="claim-status-current">{CLAIM_STATUS[claim.status]}</p>
                <p className="text-sm text-gray-600">{fmtEuro(claim.amount)}</p>
              </div>
            </div>
            <Timeline items={[...claim.timeline].reverse()} />
          </div>
        )}
      </section>
    </PublicShell>
  );
}
